
import {
  computed, defineComponent, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import MultiSelect from 'primevue/multiselect';
import {
  buildDataSourceTree,
  DataSourceOption,
  syncDataSourceSelection,
} from '@/composables/data-sources/dataSourceTree';

/**
 * Liste des points de vente : une ligne par niveau (parent avec le nombre de
 * ses magasins, magasins en retrait, source seule), sélection en cascade et
 * bouton « tous les points de vente ». Partagée par le sélecteur du header et
 * le filtre « Points de vente » des pages de statistiques, qui gardent chacun
 * leur façon d'enregistrer la sélection.
 */
export default defineComponent({
  name: 'DataSourceTreeSelect',

  components: { MultiSelect },

  props: {
    modelValue: {
      type: Array as PropType<number[]>,
      default: () => [],
    },

    sources: {
      type: Array as PropType<DataSourceOption[]>,
      default: () => [],
    },

    // Identifie une option dans la liste : deux points de vente peuvent porter
    // le même nom, leur identifiant est unique.
    dataKey: {
      type: String,
      default: 'value',
    },

    loading: {
      type: Boolean,
      default: false,
    },

    filterThreshold: {
      type: Number,
      default: 6,
    },
  },

  emits: ['update:modelValue', 'hide'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const tree = computed(() => buildDataSourceTree(props.sources));

    const isAllSelected = computed(() => props.sources.length > 0
      && props.modelValue.length === props.sources.length);

    const valueLabel = computed(() => {
      if (isAllSelected.value || props.modelValue.length === 0) {
        return t('spmTableApi.dataSourceSelector.allSources');
      }
      if (props.modelValue.length === 1) {
        const source = tree.value.items.find((s) => s.value === props.modelValue[0]);
        return source ? source.shortLabel : '1 source';
      }
      return t('spmTableApi.dataSourceSelector.nSelected', { count: props.modelValue.length });
    });

    const valueIcon = computed(() => {
      if (isAllSelected.value || props.modelValue.length === 0) return 'far fa-layer-group';
      if (props.modelValue.length === 1) {
        const source = tree.value.items.find((s) => s.value === props.modelValue[0]);
        return source?.isChild ? 'far fa-store' : 'far fa-database';
      }
      return 'far fa-database';
    });

    const onUpdate = (next: number[] | null) => {
      emit('update:modelValue', syncDataSourceSelection(
        props.modelValue,
        next ?? [],
        tree.value.childrenMap,
      ));
    };

    const selectAll = () => {
      emit('update:modelValue', props.sources.map((s) => s.value));
    };

    const getChildCount = (parentId: number): number => tree.value.childrenMap.get(parentId)?.length || 0;

    return {
      t,
      tree,
      isAllSelected,
      valueLabel,
      valueIcon,
      onUpdate,
      selectAll,
      getChildCount,
    };
  },
});
